// `/imposta-password` — first-login set-password page for intermediary
// users provisioned by PR-LP1.
//
// State machine
// -------------
//   no token in URL                          → "Link non valido"
//   validate loading                         → centered spinner
//   validate error                           → ValidateErrorView (404/410/other)
//   validate ok                              → set-password form
//   complete loading                         → submit button "Salvataggio…"
//   complete error                           → in-form Banner via CompleteErrorMessage
//   complete success                         → "Password impostata"
//
// Why this page is mounted OUTSIDE AuthGate
// -----------------------------------------
// The user reaches this URL from an email link, almost always while
// unauthenticated. AuthGate would otherwise show "Sessione non attiva"
// and the link would be unusable. The top-level dispatcher in
// `App.tsx` routes `/imposta-password` directly to this page, before
// any auth check.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GlassCard } from '@ab-platform/ui/glass-card';
import { Banner } from '@ab-platform/ui/banner';
import { PillButton } from '@ab-platform/ui/pill-button';
import { DataList, DataListRow } from '@ab-platform/ui/data-list';
import { FloatingLabelInput } from '@ab-platform/ui/floating-label-input';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { LoadingState } from '@/components/shared/LoadingState';
import { formatDateTime } from '@/lib/format';
import { useValidateProvisioningToken } from '../hooks/useValidateProvisioningToken';
import { useCompleteProvisioning } from '../hooks/useCompleteProvisioning';
import {
  MIN_PASSWORD_LENGTH,
  validatePasswordPair,
} from '../lib/password-validation';
import { ValidateErrorView } from '../components/ValidateErrorView';
import { CompleteErrorMessage } from '../components/CompleteErrorMessage';

export function SetPasswordPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const validate = useValidateProvisioningToken(token);
  const complete = useCompleteProvisioning();

  // Token + password live ONLY in component state and the in-flight
  // mutation body — never localStorage, never sessionStorage.
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [feError, setFeError] = useState<string | null>(null);

  // Drop the token from the address bar after a successful complete.
  // Vite dev + Firebase Hosting both serve `/imposta-password` from
  // the SPA fallback, so the rewrite is safe — the route still
  // resolves to this page if the user hits refresh.
  useEffect(() => {
    if (complete.isSuccess && typeof window !== 'undefined') {
      window.history.replaceState(null, '', '/imposta-password');
    }
  }, [complete.isSuccess]);

  if (!token) {
    return (
      <FocusedPageShell title="Link non valido">
        <Banner tone="warning">
          Il link per impostare la password non è valido o è incompleto.
          Apri di nuovo l’email che hai ricevuto da Aurino Broker e usa il pulsante "Imposta password".
        </Banner>
      </FocusedPageShell>
    );
  }

  if (validate.isLoading || validate.isPending) {
    return (
      <FocusedPageShell title="Imposta la password">
        <LoadingState message="Verifica del link…" />
      </FocusedPageShell>
    );
  }

  if (validate.isError) {
    return <ValidateErrorView error={validate.error} />;
  }

  const data = validate.data;

  if (complete.isSuccess) {
    return (
      <FocusedPageShell title="Password impostata">
        <Banner tone="success">
          La password è stata salvata correttamente.
        </Banner>
        <p className="text-sm leading-relaxed text-muted-foreground">
          La schermata di accesso sarà disponibile a breve. Riceverai una conferma
          da Aurino Broker quando il backoffice avrà completato l’abilitazione operativa.
        </p>
      </FocusedPageShell>
    );
  }

  return (
    <FocusedPageShell
      title="Imposta la password"
      description={
        data.fullName
          ? `Ciao ${data.fullName}, crea la password per accedere all’area intermediari.`
          : 'Crea la password per accedere all’area intermediari.'
      }
    >
      <GlassCard className="p-6">
        <DataList>
          <DataListRow label="Email" value={data.email} />
          <DataListRow label="Link valido fino a" value={formatDateTime(data.expiresAt)} />
        </DataList>
      </GlassCard>

      <form
        className="mt-6 flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          const validationError = validatePasswordPair(password, confirm);
          if (validationError) {
            setFeError(validationError);
            return;
          }
          setFeError(null);
          complete.mutate({ token, password });
        }}
      >
        <FloatingLabelInput
          type="password"
          label="Password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          helperText={`Usa almeno ${MIN_PASSWORD_LENGTH} caratteri.`}
          required
          minLength={MIN_PASSWORD_LENGTH}
        />
        <FloatingLabelInput
          type="password"
          label="Conferma password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={MIN_PASSWORD_LENGTH}
        />

        {feError ? <Banner tone="danger">{feError}</Banner> : null}
        {complete.isError ? (
          <Banner tone="danger">
            <CompleteErrorMessage error={complete.error} />
          </Banner>
        ) : null}

        <div className="flex justify-center">
          <PillButton
            type="submit"
            variant="primary"
            size="md"
            disabled={complete.isPending}
          >
            {complete.isPending ? 'Salvataggio…' : 'Imposta password'}
          </PillButton>
        </div>
      </form>
    </FocusedPageShell>
  );
}

export default SetPasswordPage;
